<template>
  <DxPopup
    :visible="popupConfig.visible"
    :drag-enabled="true"
    :close-on-outside-click="false"
    :show-title="true"
    :width="'90%'"
    :height="'80%'"
    :title="'Select Sales Orders'"
    :showCloseButton="false"
    :wrapperAttr="{
      class: 'css-v2 gc-popup gc-popup-distribution-manifest-add-order',
    }"
    @shown="onPopupShown"
  >
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
    />
    <div class="css-v2 transaction-list">
      <!-- <div class="dm-add-order-hint">
        Active sales orders are selectable. On Hold orders and orders for a Stop
        Credit customer are shown but disabled.
      </div> -->
      <gc-filter-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
        :rightContainerWidth="250"
      >
        <template #leftContent>
          <dx-data-grid
            v-if="popupConfig.visible"
            :columnChooser="{
              enabled: false,
            }"
            :pager="{ visible: false }"
            :columns="getPopupColumns"
            :data-source="dataSourceConfig"
            :row-alternation-enabled="true"
            :selection="{
              mode: 'multiple',
            }"
            :key-expr="'Code'"
            :height="getGridHeight"
            :width="'100%'"
            @toolbar-preparing="onToolbarPreparing($event)"
            @selection-changed="onSelectionChanged"
            @row-prepared="onRowPrepared"
            @content-ready="onContentReady"
            :ref="'add-order-grid'"
          >
            <DxSearchPanel :visible="false" />
            <template #Status="{ data }">
              <GcStatusChip
                v-if="isStopCredit(data.data)"
                :status="'Credit Hold'"
                class="dm-credit-hold-chip"
              />
              <GcStatusChip
                :status="data.data[data.column.dataField]"
                v-else-if="data.data[data.column.dataField]"
              />
            </template>

            <template #Record="{ data }">
              <GcRecord
                :data="data"
                :primary="data.column['cellTemplateRecord']['primary']"
                :secondary="data.column['cellTemplateRecord']['secondary']"
              />
            </template>

            <template #Decimal="{ data }">
              <GcDecimal :dataValue="data" :format="data.column.format" />
            </template>
          </dx-data-grid>
        </template>
        <template #rightContent>
          <gc-filter-panel
            :toolbarVisible="true"
            :toolbar="filterToolbar"
            :formItemData="filterFormItems"
            :setGridPanelFilter="setGridPanelFilter"
            :gridFoolter="false"
            :showLoading="showLoading"
            :from="'model'"
            :isNormal="false"
            :persistenceKey="undefined"
            :key="'fp-distribution-manifest-add-order-' + filterDefaultsReady"
            :overWriteClear="true"
            :ref="'gc-filter-panel'"
            :heightAdjustment="filterHeightAdjustment"
          >
          </gc-filter-panel>
        </template>
      </gc-filter-split-view>
    </div>
  </DxPopup>
</template>

<script>
import BaseViewMixin from "../../v1/_mixins/base.view";
import { DistributionManifestAddOrderListService } from "../../_services/api/sales/distribution-manifest-add-order-list.service";
import { BranchService } from "../../_services/api/sales/branch.service";
import { SettingsService } from "../../_services/api/settings/graycorp/settings.service";
import DataGridMixin from "@/_mixins/data-grid.mixin";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";

export default {
  props: {
    popupConfig: {
      type: Object,
      default: () => {},
    },
    addSelectedOrders: {
      type: Function,
      default: () => {},
    },
    linkedSalesOrderCodes: {
      type: Array,
      default: () => [],
    },
    currentManifestRecordId: {
      type: String,
      default: null,
    },
  },
  mixins: [BaseViewMixin, DataGridMixin],
  data() {
    return {
      showLeftContainer: true,
      showRightContainer: true,
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
          },
        },
      },
      selectedItems: [],
      initiateDataLoading: false,
      defaultFilterValues: [],
      setGridHeight: "auto",
      filterHeightAdjustment: 0,
      defaultBranchCodes: [],
      filterDefaultsReady: false,
    };
  },
  async mounted() {},
  async created() {
    await this.resolveDefaultBranchFilter();
  },
  computed: {
    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "Select",
            stylingMode: "contained",
            type: "success",
            onClick: this.confirmSelection,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          toolbar: "top",
          visible: true,
          options: {
            text: "Cancel",
            stylingMode: "outlined",
            type: "danger",
            onClick: this.popupHidden,
          },
        },
      ];
    },

    getGridHeight() {
      return this.setGridHeight;
    },

    shipDateDefaultValue() {
      const date = new Date();
      date.setDate(date.getDate() + 7);
      return date;
    },

    getPopupColumns() {
      return this.parseColumnWithScreenFields([
        {
          caption: "",
          dataField: "",
          type: "selection",
          fixed: true,
          fixedPosition: "left",
          width: 30,
          minWidth: 30,
        },
        {
          screenField: "SalesOrder",
          caption: "Sales Order No.",
          gridDataField: "CodeComputedString",
          dataField: "CodeComputedString",
          calculateSortValue: "Code",
          width: ColumnWidth.SalesOrderNumber.width + 20,
          minWidth: ColumnWidth.SalesOrderNumber.minWidth + 20,
          allowSorting: true,
        },
        {
          screenField: "Customer",
          allowSorting: true,
          minWidth: 180,
        },
        {
          caption: "Delivery Address",
          dataField: "DeliveryAddress",
          minWidth: 220,
          calculateCellValue: (rowData) => this.deliveryAddressDisplay(rowData),
        },
        {
          screenField: "State",
          gridDataField: "State",
          dataField: "State",
          allowSorting: true,
          width: 100,
          minWidth: 100,
        },
        {
          caption: "Delivery Date",
          gridDataField: "DeliveryDate",
          dataField: "DeliveryDate",
          dataType: "date",
          format: "dd MMM yyyy",
          allowSorting: true,
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          screenField: "ShipDate",
          caption: "Ship Date",
          dataType: "date",
          format: "dd MMM yyyy",
          allowSorting: true,
          sortOrder: "asc",
          sortIndex: 0,
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
        },
        {
          caption: "Pallets",
          dataField: "Pallets",
          dataType: "number",
          minWidth: 50,
          width: 50,
          format: "###,###,##0",
          cellTemplate: "Decimal",
        },
        {
          caption: "Layers",
          dataField: "Layers",
          dataType: "number",
          minWidth: 50,
          width: 50,
          format: "###,###,##0",
          cellTemplate: "Decimal",
        },
        {
          caption: "Goods Weight",
          dataField: "GoodsWeight",
          dataType: "number",
          minWidth: 70,
          width: 70,
          format: "###,###,##0.0000",
          cellTemplate: "Decimal",
        },
        {
          screenField: "OrderStatus",
          caption: "Status",
          gridDataField: "FriendlyStatus",
          dataField: "FriendlyStatus",
          cellTemplate: "Status",
          width: 90,
          minWidth: 90,
        },
      ]);
    },

    dataSourceConfig() {
      if (this.initiateDataLoading) {
        return this.originalDataSourceConfig;
      } else {
        return [];
      }
    },
    originalDataSourceConfig() {
      // currentManifestRecordId is only passed when editing an already-saved manifest
      // (see distribution-manifest.vue) — the API uses it to compute AlreadyOnThisManifest,
      // which is meaningless for a brand-new, not-yet-saved manifest with no linked orders.
      const url = this.currentManifestRecordId
        ? `api/DistributionManifestAddOrderList?currentManifestRecordId=${this.currentManifestRecordId}`
        : "api/DistributionManifestAddOrderList";

      return DistributionManifestAddOrderListService.dataSourceConfig(url, {
        key: "Code",
        filter: this.baseFilter,
      });
    },

    baseFilter() {
      let filter = [
        ["FriendlyStatus", "=", "Active"],
        "or",
        ["FriendlyStatus", "=", "On Hold"],
      ];

      // Alternative to the shade-and-validate-on-Select handling in orderBlockReason()/
      // isAlreadyOnManifest() below: filter already-linked orders out of the picker
      // entirely instead of showing them disabled. Simpler, and matches how this worked
      // before ADR 22/AlreadyOnThisManifest existed — but the row then just silently
      // disappears rather than showing the user why. Left commented pending a decision
      // with the PO on which behaviour is actually wanted; swap this in and drop the
      // "already added" branch of orderBlockReason() if filtering wins out.
      // if (this.linkedSalesOrderCodes && this.linkedSalesOrderCodes.length) {
      //   let combined = [filter];
      //   this.linkedSalesOrderCodes.forEach((code) => {
      //     combined.push("and");
      //     combined.push(["Code", "<>", code]);
      //   });
      //   return combined;
      // }

      return filter;
    },

    /**
     * Retun filter panel form items
     * filterIndex field does have an impact , because we directly inject the fields in to the filter panel.
     * since it's a direct inject, field order is the order we define it. - SD
     */
    filterFormItems() {
      return [
        {
          screenField: "Code",
          filterLabel: "Sales Order No.",
          filterIndex: 0,
          type: "Integer",
        },
        {
          screenField: "CustPO",
          filterLabel: "Customer PO",
          filterDataField: "OrderNo",
          filterIndex: 1,
          type: "Text",
        },
        {
          screenField: "Customer",
          filterIndex: 2,
        },
        {
          screenField: "Branch",
          filterIndex: 3,
          defaultValue: this.defaultBranchCodes,
        },
        {
          screenField: "Market",
          dataField: "MarketCode",
          filterDataField: "MarketCode",
          filterIndex: 4,
          filterDefaultValue: ["Domestic"],
        },
        {
          screenField: "ShipDate",
          filterLabel: "Ship Date (on or before)",
          filterIndex: 5,
          filterOperator: "<=",
          filterDefaultValue: this.shipDateDefaultValue,
        },
        {
          screenField: "RequiredByDate",
          filterLabel: "Required Date (on or before)",
          filterDataField: "DeliveryDate",
          filterIndex: 6,
          filterOperator: "<=",
        },
      ];
    },
  },
  methods: {
    async onPopupShown() {
      setTimeout(async () => {
        this.setGridHeight = this.filterHeightAdjustment =
          document.querySelectorAll(
            ".gc-popup-distribution-manifest-add-order .dx-popup-content",
          )[0].clientHeight - 20;

        this.initiateDataLoading = true;
      }, 1);

      this.$nextTick(() => {
        this.reSizeGrid();
      });
    },
    popupHidden() {
      this.popupConfig.visible = false;
    },

    /**
     * Resolves the DefaultAddOrderBranchFilter ApplicationFeatureSetting
     * (ApplicationFeatureCode='DistributionManifest', ScopeCode='Global') into
     * defaultBranchCodes, validating each comma-separated code against real
     * branches so invalid codes are silently dropped rather than blocking the
     * remaining valid ones. Blank/unconfigured/fully-invalid resolves to [].
     */
    async resolveDefaultBranchFilter() {
      try {
        const response = await SettingsService.getBySettingName(
          "DefaultAddOrderBranchFilter",
        );
        const settings = (response && response.data && response.data.value) || [];
        const setting = settings.find(
          (s) =>
            s.ApplicationFeatureCode === "DistributionManifest" &&
            s.ScopeCode === "Global",
        );

        const codes = (setting && setting.SettingValue ? setting.SettingValue : "")
          .split(",")
          .map((code) => code.trim())
          .filter((code) => code.length > 0);

        this.defaultBranchCodes =
          codes.length > 0 ? await BranchService.getValidCodes(codes) : [];
      } catch (e) {
        this.defaultBranchCodes = [];
      } finally {
        this.filterDefaultsReady = true;
      }
    },

    isStopCredit(data) {
      return data && data.StopCredit === "Y";
    },

    /**
     * "<DeliveryFirm> - <DeliveryAddress>", dropping either side (and its
     * separator) when blank, per Task 74470.
     */
    deliveryAddressDisplay(rowData) {
      const firm = (rowData && rowData.DeliveryFirm) || "";
      const address = (rowData && rowData.DeliveryAddress) || "";

      if (firm && address) return `${firm} - ${address}`;
      return firm || address;
    },

    /**
     * True once the order is already linked to the manifest currently being edited —
     * either confirmed server-side (AlreadyOnThisManifest, scoped to currentManifestRecordId)
     * or added client-side this session but not yet saved (linkedSalesOrderCodes), which the
     * server flag can't see yet. A second row for it would be a pure duplicate (ADR 22).
     */
    isAlreadyOnManifest(order) {
      return (
        order &&
        (order.AlreadyOnThisManifest === "Y" ||
          this.linkedSalesOrderCodes.includes(order.Code))
      );
    },

    orderBlockReason(order) {
      // Checked first: a duplicate row is a hard data-integrity block, not a business
      // judgment call like On Hold/Stop Credit below, but rows still can't be disabled
      // at the checkbox/cell level — DevExtreme's "Select All" drives the selection model
      // directly and bypasses a pointer-events-blocked cell, leaving the row stuck in an
      // inconsistent checked-but-unclickable state. So it's shaded + selectable here too,
      // same as the other reasons, and only actually blocked at the Select button.
      if (this.isAlreadyOnManifest(order)) {
        return {
          blocked: true,
          label: "Already added to this Distribution Manifest",
          rowClass: "row-already-added",
        };
      }
      if (this.isStopCredit(order)) {
        return {
          blocked: true,
          label: "Customer on Stop Credit",
          rowClass: "row-stopcredit",
        };
      }
      if (order && order.FriendlyStatus === "On Hold") {
        return {
          blocked: true,
          label: "Order On Hold",
          rowClass: "row-onhold",
        };
      }
      return { blocked: false, label: "", rowClass: "" };
    },

    /**
     * Filter Panel values
     * @param e
     * @param valueChanged
     */

    // eslint-disable-next-line no-unused-vars
    setGridPanelFilter(e, valueChanged) {
      if (!this.$refs["add-order-grid"]) return;

      let dataGrid = this.$refs["add-order-grid"].instance;

      if (
        e.length === 0 &&
        valueChanged &&
        this.defaultFilterValues.length > 0
      ) {
        dataGrid.filter(this.defaultFilterValues);
        dataGrid.refresh(true);
      } else {
        if (this.initiateDataLoading == false) {
          dataGrid.option("dataSource", this.originalDataSourceConfig);
          this.initiateDataLoading = true;
          if (e.length > 0) this.defaultFilterValues = [...e];
        }

        if (dataGrid) {
          if (e.length > 0) dataGrid.filter(e);
          else dataGrid.clearFilter();

          dataGrid.refresh(true);
        }
      }
    },

    onToolbarPreparing(e) {
      e.toolbarOptions.items.push({
        location: "after",
        locateinmenu: "auto",
        widget: "dxButton",
        options: {
          validationGroup: "formsData",
          icon: "filter",
          elementAttr: { id: "add-order-filter-btn" },
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
            var item = document.getElementById("add-order-filter-btn");
            item.classList.add("dx-state-invisible");

            setTimeout(() => {
              this.reSizeGrid();
            }, 50);
          },
          visible: !this.showRightContainer,
        },
      });
    },

    onSelectionChanged(e) {
      this.selectedItems = e.selectedRowsData;
    },

    onRowPrepared(e) {
      if (e.rowType !== "data") return;

      const block = this.orderBlockReason(e.data);
      if (block.blocked) {
        e.rowElement.classList.add(block.rowClass);
        e.rowElement.setAttribute("title", block.label);
      }
    },

    onContentReady(e) {
      this.addGridNoResults(e, undefined);
    },

    async confirmSelection() {
      if (this.selectedItems.length === 0) {
        this.$notify(
          "Please select at least one order to add to the Distribution Manifest.",
          "error",
        );
        return;
      }

      const invalidOrders = this.selectedItems.filter(
        (o) => this.orderBlockReason(o).blocked,
      );

      if (invalidOrders.length > 0) {
        const reasons = new Set(
          invalidOrders.map((o) => this.orderBlockReason(o).label),
        );
        this.$alert(
          `One or more selected orders cannot be added to this Distribution Manifest (${Array.from(
            reasons,
          ).join("; ")}).`,
          "Invalid Orders Selected",
        );
        return;
      }

      // addSelectedOrders now resolves each selected order's Resolved UM
      // breakdown server-side before adding lines — await it so the popup
      // doesn't close until the lines have actually been added.
      await this.addSelectedOrders(this.selectedItems);
      this.popupHidden();
    },
  },
};
</script>

<style lang="scss">
.gc-popup-distribution-manifest-add-order {
  .dm-add-order-hint {
    font-size: 11px;
    color: #667085;
    padding: 0 2px 6px;
  }

  .row-onhold td {
    background: #fff4e5 !important;
  }

  .row-stopcredit td {
    background: #fdeded !important;
  }

  .row-already-added td {
    background: #f2f2f2 !important;
    color: #98a2b3;
  }

  .dm-credit-hold-chip {
    margin-left: 4px;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:first-of-type:not(.dx-command-expand) {
    padding-left: 10px !important;
    padding-right: 10px !important;
  }

  .dx-datagrid
    .dx-datagrid-content
    .dx-datagrid-table
    .dx-row
    > td:not(.dx-validation-pending):not(.dx-datagrid-select-all) {
    vertical-align: top !important;
  }

  .advance-filter-panel .dx-form {
    padding-bottom: 10px !important;
  }
}
</style>
